import type { UiEvents } from "@keystone-ui/types";

import { useEligibleOffers } from "components/offers/hooks";
import { usePolicyId, useUiEvents, useUser } from "data/ss-data-hooks";
import { useFlags } from "utils";

type NotificationStatus = {
  offerInternalName: string;
  read: boolean;
  dismissed: boolean;
};

export type NotificationDetail = {
  offerTitle: string;
  offerDescription: string;
  offerInternalName: string;
  path?: string;
  read: boolean;
  dismissed: boolean;
  buttonText?: string;
};

const offersDeclinedMap: Record<string, string> = {
  "wind-defense": "wind-defense-landing-declined",
  "ting-offer": "ting-landing-declined",
};

// Titles must match the offering internal name
const titleDescriptionMap: Record<
  string,
  { title: string; description: string; buttonText?: string }
> = {
  "marketing-optin": {
    title: "Join our mailing list and get a free home maintenance checklist",
    description: `Get SageSure offers, news, and expert tips to protect your home, delivered to your inbox.`,
    buttonText: "Get Started",
  },
  "wind-defense": {
    title: "Strengthen your roof for free with Wind Defense",
    description: `If your home is selected, a licensed technician will remove debris, clean gutters, and reseal shingles.`,
    buttonText: "Learn about Wind Defense",
  },
};

//use this function to get the current list of available notifications
//in the notifications obj
const getNotificatonState = (
  uiEvents: UiEvents | undefined,
  policyId: string | undefined,
): NotificationStatus[] => {
  const notificationsUiEvent = uiEvents?.events?.notifications;
  if (!policyId) {
    return [];
  }

  const dismissedNotifications =
    notificationsUiEvent?.[policyId]?.dismissed ?? [];
  const readNotifications = notificationsUiEvent?.[policyId]?.read ?? [];

  const offerInternalNames = new Set([
    ...dismissedNotifications,
    ...readNotifications,
  ]);

  return [...offerInternalNames].map((offerInternalName) => ({
    offerInternalName,
    read: readNotifications.includes(offerInternalName),
    dismissed: dismissedNotifications.includes(offerInternalName),
  }));
};

export const useNotifications = () => {
  const { notificationPilot } = useFlags();
  const notificationPilotEnabled = notificationPilot === true;

  const user = useUser();
  const selectedPolicyId = usePolicyId();
  const { data: uiEvents } = useUiEvents();
  const { eligibleOffers } = useEligibleOffers(
    notificationPilotEnabled ? selectedPolicyId : undefined,
  );
  const notificationStateMap = getNotificatonState(uiEvents, selectedPolicyId);

  if (!notificationPilotEnabled) {
    return [];
  }

  const notifications: NotificationDetail[] = eligibleOffers.unenrolledOffers
    .filter((offer) => offer.offeringInternalName in titleDescriptionMap)
    .filter((offer) => {
      const declinedPreferenceName =
        offersDeclinedMap[offer.offeringInternalName];
      const declinedPreference = user?.accountPreferences?.find(
        (pref) => pref.preferenceName === declinedPreferenceName,
      );
      return !declinedPreference?.preferenceValue;
    })

    .map((offer) => {
      const status = notificationStateMap.find(
        (s) => s.offerInternalName === offer.offeringInternalName,
      );
      return {
        offerTitle: titleDescriptionMap[offer.offeringInternalName]?.title,
        offerDescription:
          titleDescriptionMap[offer.offeringInternalName]?.description,
        offerInternalName: offer.offeringInternalName,
        path: offer.offerUrl,
        read: status?.read ?? false,
        dismissed: status?.dismissed ?? false,
        buttonText: titleDescriptionMap[offer.offeringInternalName]?.buttonText,
      };
    });

  const marketingPreference = user?.accountPreferences?.find(
    (pref) => pref.preferenceName === "marketing-optin",
  );
  const isMarketingEligible = !marketingPreference?.preferenceValue;

  if (isMarketingEligible) {
    const status = notificationStateMap.find(
      (s) => s.offerInternalName === "marketing-optin",
    );
    const map = titleDescriptionMap["marketing-optin"];
    const marketingNotification = {
      offerTitle: map.title,
      offerDescription: map.description,
      offerInternalName: "marketing-optin",
      read: status?.read ?? false,
      dismissed: status?.dismissed ?? false,
      buttonText: map.buttonText,
    };
    notifications.push(marketingNotification);
  }

  return notifications;
};
